import { useEffect, useState, useContext } from 'react';
import { ProfileService } from '../../api/profile';
import { useAuth } from '../../hooks/useAuth';
import { AccessContext, ProfileAccess } from './AccessContext';

const DEFAULT_USER_ROLE = 'user:waitlist';

interface AccessProviderProps {
  children: React.ReactNode;
}

export function AccessProvider({ children }: AccessProviderProps) {
  const [access, setAccess] = useState<ProfileAccess | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(false);
  const { isAuthenticated, isLoading: isAuthLoading } = useAuth();

  useEffect(() => {
    const fetchAccess = async () => {
      setIsLoading(true);
      setError(false);
      try {
        const data = await ProfileService.access();
        setAccess({
          role: data.role ?? DEFAULT_USER_ROLE,
          features: new Set(data.features || []),
          permissions: new Set(Object.keys(data.permissions)),
          resources: new Map(
            Object.keys(data.permissions).map((k) => [k, new Set(data.permissions[k].resources)]),
          ),
          subscription_start_date: data.subscription_start_date ?? null,
          subscription_end_date: data.subscription_end_date ?? null,
          subscription_active: data.subscription_active ?? false,
        });
      } catch {
        setError(true);
        // TODO: Rather redirect the user to a new generic error stating
        // something went wrong.
        setAccess({
          role: DEFAULT_USER_ROLE,
          features: new Set(),
          permissions: new Set(),
          resources: new Map(),
          subscription_start_date: null,
          subscription_end_date: null,
          subscription_active: false,
        });
      } finally {
        setIsLoading(false);
      }
    };

    if (isAuthLoading) {
      setIsLoading(true);
      setAccess(null);
      setError(false);
      return;
    }

    fetchAccess();
  }, [isAuthLoading, isAuthenticated]);

  return (
    <AccessContext.Provider value={{ access, isLoading, error }}>{children}</AccessContext.Provider>
  );
}

export function useAccess() {
  return useContext(AccessContext);
}
